import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { LoaderCircle } from "lucide-react";
import { cn } from "@/utils/helpers";
import type { CalendarSlotIdea } from "@/types/contentCalendar.type";
import ElapsedSince from "../ElapsedSince";
import IdeaSlide, { SLIDE_H, SLIDE_W, type IdeaSlideData } from "./IdeaSlide";

/**
 * The ideas page (Figma 08, 3 Ideas): one idea as one slide, the three ideas
 * as a strip under it, and Write yours as the strip's last tile. Selecting a
 * thumbnail puts that idea on the slide; the header's Pick idea N picks it.
 * Desktop and tablet; the phone keeps its one-idea-per-screen story.
 */

const SLIDE_MAX = 1160;

/** The slide's box at the canvas scale, for a skeleton or an overlay drawn in canvas units. */
function CanvasBox({ width, children, className }: { width?: number; children: ReactNode; className?: string }) {
  const box = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(width ? width / SLIDE_W : 0);
  useLayoutEffect(() => {
    if (width) return;
    const node = box.current;
    if (!node) return;
    const measure = () => setScale(node.getBoundingClientRect().width / SLIDE_W);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [width]);
  return (
    <div
      ref={box}
      className={cn("relative w-full overflow-hidden bg-co-surface", className)}
      style={width ? { width, height: (width * SLIDE_H) / SLIDE_W } : { aspectRatio: `${SLIDE_W} / ${SLIDE_H}` }}
    >
      <div
        className="absolute left-0 top-0 origin-top-left"
        style={{ width: SLIDE_W, height: SLIDE_H, transform: `scale(${scale})`, visibility: scale ? "visible" : "hidden" }}
      >
        {children}
      </div>
    </div>
  );
}

const BAR = "rounded-[7px] bg-co-tonal motion-safe:animate-pulse";

/** The slide while Creo writes it: its shape in bars, the two fact cards as bones too. */
export function IdeaSlideSkeleton({ width, className }: { width?: number; className?: string }) {
  return (
    <CanvasBox width={width} className={className}>
      <div aria-hidden="true" className="flex size-full gap-[60px] px-[80px] py-[50px]">
        <div className="flex min-w-0 flex-1 flex-col">
          <span className={cn(BAR, "h-[40px] w-[770px]")} />
          <span className={cn(BAR, "mt-[16px] h-[40px] w-[500px]")} />
          <span className="mt-[30px] flex gap-[10px]">
            <span className={cn(BAR, "h-[30px] w-[150px]")} />
            <span className={cn(BAR, "h-[30px] w-[60px]")} />
            <span className={cn(BAR, "h-[30px] w-[60px]")} />
            <span className={cn(BAR, "h-[30px] w-[70px]")} />
          </span>
          <span className={cn(BAR, "mt-[30px] h-[16px] w-full")} />
          <span className={cn(BAR, "mt-[12px] h-[16px] w-full")} />
          <span className={cn(BAR, "mt-[12px] h-[16px] w-[540px]")} />
          <span className={cn(BAR, "mt-[30px] h-[14px] w-[60px]")} />
          {[0, 1, 2].map((row) => (
            <span key={row} className="mt-[22px] flex gap-[20px]">
              <span className={cn(BAR, "h-[16px] w-[30px]")} />
              <span className={cn(BAR, "h-[16px] flex-1")} />
            </span>
          ))}
          <span className="mt-[34px] flex gap-[15px]">
            {[120, 100].map((chip) => (
              <span key={chip} className="flex h-[118px] flex-1 flex-col gap-[14px] rounded-[15px] border border-co-line p-[15px]">
                <span className={cn(BAR, "h-[30px]")} style={{ width: chip }} />
                <span className={cn(BAR, "h-[14px] w-4/5")} />
                <span className={cn(BAR, "h-[14px] w-3/5")} />
              </span>
            ))}
          </span>
          <span className={cn(BAR, "mt-[30px] h-[14px] w-[100px]")} />
          <span className={cn(BAR, "mt-[16px] h-[14px] w-full")} />
          <span className={cn(BAR, "mt-[12px] h-[14px] w-[640px]")} />
          <span className="mt-auto flex gap-[10px]">
            <span className={cn(BAR, "h-[33px] w-[33px]")} />
            <span className={cn(BAR, "h-[33px] w-[33px]")} />
            <span className={cn(BAR, "h-[33px] w-[33px]")} />
            <span className={cn(BAR, "ml-[12px] h-[16px] w-[360px] self-center")} />
          </span>
        </div>
        <span className={cn(BAR, "h-[800px] w-[450px] shrink-0 rounded-[17.5px]")} />
      </div>
    </CanvasBox>
  );
}

/** The card over a slide while machine work runs (Figma: "Creo writes the ideas · 0:34 so far"). */
export function SlideWaitCard({ title, since, line, children }: { title: string; since?: string | null; line: string; children?: ReactNode }) {
  return (
    <div className="pointer-events-none absolute inset-0 grid place-items-center p-4">
      <div role="status" className="flex max-w-[460px] flex-col items-center gap-2 rounded-card bg-co-surface-solid px-7 py-5 text-center shadow-[0_8px_30px_rgba(0,0,0,0.35)]">
        <LoaderCircle className="size-5 text-co-muted motion-safe:animate-spin" strokeWidth={1.6} aria-hidden="true" />
        <p className="text-[15px] font-semibold leading-snug text-co-text">
          {title}
          <ElapsedSince since={since} />
        </p>
        <p className="text-[13px] leading-[1.5] text-co-text2">{line}</p>
        {children}
      </div>
    </div>
  );
}

function WriteYoursTile({ onPress, selected = false }: { onPress?: () => void; selected?: boolean }) {
  return (
    <li className="flex min-w-0 flex-col gap-2">
      <button
        type="button"
        onClick={onPress}
        disabled={!onPress && !selected}
        aria-pressed={selected}
        className={cn(
          "grid aspect-[196/110] w-full place-items-center rounded-[8px] text-[13.5px] font-medium text-co-text2 outline-none transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent disabled:opacity-50",
          selected
            ? "shadow-[0_0_0_2px_var(--co-accent)]"
            : "border border-dashed border-co-faint hover:border-co-text2 hover:text-co-text",
        )}
      >
        Write yours
      </button>
      <span className="text-[12.5px] leading-[17px] text-co-muted">Your own idea{selected ? " · selected" : ""}</span>
    </li>
  );
}

export interface IdeaSlideBoardProps {
  ideas: CalendarSlotIdea[];
  /** The slide's words for one idea (its position among the three). */
  slideFor: (idea: CalendarSlotIdea, index: number) => IdeaSlideData;
  selectedIdeaId: string | null;
  onSelectIdea?: (ideaId: string) => void;
  onChangeMedia?: (ideaId: string) => void;
  onWriteOwn?: () => void;
  /** The idea being rewritten, when one is: where the rewrite came from and since when. */
  rewriting?: { ideaId: string; since: string | null; from: "chat" | "angle" | "own" | null } | null;
  readOnly?: boolean;
  /** Write your own idea, open in the slide's place: its form, with Write yours selected in the strip. */
  own?: ReactNode;
}

export default function IdeaSlideBoard({
  ideas,
  slideFor,
  selectedIdeaId,
  onSelectIdea,
  onChangeMedia,
  onWriteOwn,
  rewriting,
  readOnly = false,
  own,
}: IdeaSlideBoardProps) {
  const shownId = own ? null : (selectedIdeaId ?? ideas[0]?.id ?? null);
  const shownIndex = Math.max(0, ideas.findIndex((idea) => idea.id === shownId));
  const shown = own ? null : (ideas[shownIndex] ?? null);
  const working = shown?.status === "WORKING" || (rewriting?.ideaId === shown?.id && !!rewriting);
  const others = ideas
    .map((idea, index) => ({ idea, index }))
    .filter(({ idea }) => idea.id !== shown?.id)
    .map(({ index }) => index + 1);

  return (
    <div className="flex flex-col gap-5">
      <div className="w-full" style={{ maxWidth: SLIDE_MAX }}>
        {own ? (
          // Figma 08: "Your own idea · in the slide's place".
          <div className="bg-co-surface px-6 pb-12 pt-9 sm:px-[58px]">{own}</div>
        ) : shown && working ? (
          <div className="relative">
            <IdeaSlideSkeleton />
            <SlideWaitCard
              title={`Rewriting idea ${shownIndex + 1}`}
              since={rewriting?.since ?? shown.updatedAt}
              line={`${
                rewriting?.from === "chat"
                  ? "From your direction in the chat."
                  : rewriting?.from === "own"
                    ? "From the idea you wrote."
                    : "A new angle on the same post."
              } ${others.length ? `Idea${others.length === 1 ? "" : "s"} ${others.join(" and ")} stay${others.length === 1 ? "s" : ""} as ${others.length === 1 ? "it is" : "they are"}.` : ""}`}
            />
            <div className="pointer-events-none absolute bottom-[4%] left-[5%] flex flex-col gap-1">
              <span className="text-[12.5px] text-co-muted">The version being replaced</span>
              <span className="text-[14px] text-co-text2">{shown.title}</span>
            </div>
          </div>
        ) : shown ? (
          <IdeaSlide
            data={slideFor(shown, shownIndex)}
            onChangeMedia={!readOnly && onChangeMedia ? () => onChangeMedia(shown.id) : undefined}
            ariaLabel={`Idea ${shownIndex + 1}: ${shown.title}`}
          />
        ) : null}
      </div>

      <ul className="m-0 grid list-none grid-cols-[repeat(4,minmax(0,196px))] gap-5 p-0 max-sm:grid-cols-2" aria-label="The three ideas">
        {ideas.map((idea, index) => {
          const picked = idea.id === shown?.id;
          const busy = idea.status === "WORKING" || rewriting?.ideaId === idea.id;
          return (
            <li key={idea.id} className="flex min-w-0 flex-col gap-2">
              <button
                type="button"
                onClick={onSelectIdea ? () => onSelectIdea(idea.id) : undefined}
                aria-pressed={picked}
                aria-label={`Idea ${index + 1}${picked ? ", selected" : ""}: ${idea.title}`}
                className={cn(
                  "relative block aspect-[196/110] w-full overflow-hidden rounded-[8px] outline-none transition-shadow",
                  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent",
                  picked && "shadow-[0_0_0_2px_var(--co-accent)]",
                  !onSelectIdea && "cursor-default",
                )}
              >
                {busy ? <IdeaSlideSkeleton /> : <IdeaSlide data={slideFor(idea, index)} facts={false} />}
              </button>
              <span className="text-[12.5px] leading-[17px] text-co-muted">
                Idea {index + 1}
                {busy ? " · rewriting" : picked ? " · selected" : ""}
              </span>
            </li>
          );
        })}
        {!readOnly ? <WriteYoursTile onPress={own ? undefined : onWriteOwn} selected={!!own} /> : null}
      </ul>
    </div>
  );
}

/**
 * The ideas page while Creo writes them: the slide's shape with the clock, three empty
 * thumbnails and Write yours as a bone too (it opens nothing until the ideas land).
 */
export function IdeaSlideBoardWriting({ since }: { since: string | null }) {
  return (
    <div className="flex flex-col gap-5">
      <div className="relative w-full" style={{ maxWidth: SLIDE_MAX }}>
        <IdeaSlideSkeleton />
        <SlideWaitCard title="Creo writes the ideas" since={since} line="You can leave. The card and Home say when they are ready." />
      </div>
      <ul className="m-0 grid list-none grid-cols-[repeat(4,minmax(0,196px))] gap-5 p-0 max-sm:grid-cols-2" aria-label="The three ideas, being written">
        {[0, 1, 2].map((index) => (
          <li key={index} className="flex min-w-0 flex-col gap-2">
            <span className="block aspect-[196/110] w-full overflow-hidden rounded-[8px]">
              <IdeaSlideSkeleton />
            </span>
            <span className="text-[12.5px] leading-[17px] text-co-muted">Idea {index + 1}</span>
          </li>
        ))}
        <li className="flex min-w-0 flex-col gap-2" aria-hidden="true">
          <span className={cn(BAR, "block aspect-[196/110] w-full rounded-[8px]")} />
          <span className={cn(BAR, "h-[17px] w-[88px]")} />
        </li>
      </ul>
    </div>
  );
}
